import { Button } from '@/components/ui/button';

import { BetweenHorizonalStart } from 'lucide-react';
import { useRouter } from 'next/navigation';
import { useRef, useState } from 'react';
import { updateCartCheckout } from '@/lib/actions/checkout/action';
import { toast } from '@/components/ui/use-toast';
import LoadingAnimationIcon from '@/components/icons/loading-animation-icon';
import { ElementProps } from '@/components/sections/builder/types';
import { useBuilderStore } from '@/stores/builder';
import { shallow } from 'zustand/shallow';
import { HttpTypes } from '@/types';
import { useMutateApplyPromotion } from '@/hooks/api/storefront/promotion';
import Cookies from 'js-cookie';
import { AxiosError } from 'axios';
import { parseErrorMessage } from '@/lib/utils';
import { usePromotionStore } from '@/stores/promotion';
import useModal from '@/hooks/useModal';
import ModalAlert from '@/components/sections/modal-alert';

const defaultData: { name: string } = {
  name: 'Checkout button',
};

export const ElementContent = ({ element, preview }: ElementProps) => {
  const router = useRouter();
  const host =
    typeof window !== 'undefined' ? window.location.host || window.location.hostname : '';
  const token = Cookies.get('accessToken') || '';
  const modalConfirmFreeGift = useModal();

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const isSubmittingRef = useRef<boolean>(false);

  const builder = useBuilderStore(
    (state) => ({
      theme: state.theme,
    }),
    shallow
  );
  const promotionStore = usePromotionStore(
    (state) => ({
      selectedPromotion: state.selectedPromotion,
      selectedFreeGift: state.selectedFreeGift,
    }),
    shallow
  );

  const themeContent = preview ? builder.theme : element.theme;
  const cart = element.parentData as HttpTypes.StoreCart;

  const hasInsufficientStock = cart?.item_stock?.some((item) => item.stock === 0);
  const isCartEmpty = !((cart?.items?.length ?? 0) > 0);
  const disableButton = isLoading || hasInsufficientStock || isCartEmpty;

  const { mutateAsync: mutateApplyPromotion } = useMutateApplyPromotion();

  const handleButtonCheckout = async () => {
    if (disableButton || isSubmittingRef.current) return;

    const promoFreeProduct =
      promotionStore.selectedPromotion.filter(
        (promo) => Array.isArray(promo.product_reward) && promo.product_reward.length > 0
      ) ?? [];

    if (promoFreeProduct.length > 0) {
      const freeGiftIds = new Set(
        (promotionStore.selectedFreeGift ?? []).map((g) => g?.promotion_id).filter(Boolean)
      );

      const missing = promoFreeProduct
        .map((p) => p?.promotion_id)
        .filter((id) => id && !freeGiftIds.has(id));

      if (missing.length > 0) {
        modalConfirmFreeGift.open();
        return;
      }
    }

    await handleSubmitCheckout();
  };

  const handleSubmitCheckout = async () => {
    if (disableButton || isSubmittingRef.current) return;
    isSubmittingRef.current = true;
    setIsLoading(true);

    try {
      const payload = cart?.promotions_payload;
      const hasPromos = !!(
        payload &&
        Array.isArray(payload.promotions) &&
        payload.promotions.length > 0
      );

      if (hasPromos) {
        await mutateApplyPromotion({
          body: payload ?? { cart_id: cart.id, promotions: [] },
          token,
        });
      }

      const result = await updateCartCheckout(host as string);

      if (result.success) {
        router.push(`/checkout/${result.data.checkout_id}`);
        return;
      }

      if (isCartEmpty) {
        toast({
          title: 'Error',
          description: 'Cart is empty',
          variant: 'destructive',
        });
        return;
      }

      if (result.status === 404) {
        await fetch('/api/remove-cart-cookie', { method: 'GET' });

        toast({
          title: 'Error',
          description: result.message,
          variant: 'destructive',
        });

        return (window.location.href = '/');
      }

      if (result.status === 400) {
        toast({
          title: 'Error',
          description: result.message,
          variant: 'destructive',
        });
        return;
      }
    } catch (error) {
      if (error instanceof AxiosError) {
        const errorMessage = error.response?.data.data || error.response?.data?.message;
        toast({
          title: 'Error',
          description: parseErrorMessage(errorMessage) || 'Something went wrong',
          variant: 'destructive',
        });

        if (error.status === 401) {
          await fetch('/api/remove-cart-cookie', { method: 'GET' });

          return (window.location.href = '/');
        }
      }
    } finally {
      isSubmittingRef.current = false;
      setIsLoading(false);
    }
  };

  return (
    <>
      <Button
        className='theme_button theme_cart_page_btn flex w-full items-center px-24 md:w-fit'
        disabled={isLoading || disableButton}
        onClick={handleButtonCheckout}
      >
        {isLoading && <LoadingAnimationIcon />}
        <span className='mx-2'>{themeContent?.cart?.labelCheckout || 'Checkout'}</span>
      </Button>

      <ModalAlert
        openModal={modalConfirmFreeGift.isShow}
        setOpenModal={modalConfirmFreeGift.setIsShow}
        isLoading={isLoading}
        onConfirm={handleSubmitCheckout}
        title='Free Gift Not Selected'
        description='You have a free gift promotion that hasn’t been selected yet. Do you want to continue checkout without choosing your gift?'
        buttonConfirmText='Continue'
        buttonCancelText='Cancel'
        colorScheme={themeContent?.cart?.pageCartColorScheme}
      />
    </>
  );
};

export const ElementPreviewIcon = BetweenHorizonalStart;

export function ElementSetting() {
  return <div></div>;
}

const CartCheckoutButton = {
  type: 'cart_checkout_button',
  group: 'cart_total',
  defaultData,
  Element: ElementContent,
  Setting: ElementSetting,
  PreviewIcon: ElementPreviewIcon,
  Icon: ElementPreviewIcon,
  childs: [],
  maxEntries: 1,
};

export default CartCheckoutButton;
